import React, { useEffect, useMemo, useState } from "react";
import { Send, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { INSTRUMENTS, getLessonContext } from "@/data/curriculum";

const allCourses = (inst) => [...inst.courses, ...(inst.miniCourses || [])];
const sel = "rounded-xl border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none";
const lessonName = (id) => { const c = getLessonContext(id); return c ? `${c.instrument.label} · ${c.course.title} · Lesson ${c.number}` : `Lesson ${id}`; };

export const ScorePublish = ({ score, body, title }) => {
  const courses = useMemo(() => INSTRUMENTS.flatMap((i) => allCourses(i).map((c) => ({ key: `${i.id}:${c.id}`, label: `${i.label} — ${c.title}`, lessons: c.lessons }))), []);
  const [courseKey, setCourseKey] = useState(courses[0]?.key || "");
  const [lessonId, setLessonId] = useState("");
  const [label, setLabel] = useState(title);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const lessons = courses.find((c) => c.key === courseKey)?.lessons || [];
  const load = () => api.get("/admin/lesson-scores").then(({ data }) => setItems(data.items.filter((x) => x.score_id === score.id))).catch(() => {});
  useEffect(() => { setLabel(title); load(); }, [score.id, title]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLessonId(String(lessons[0]?.id || "")); }, [courseKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/scores/${score.id}/publish`, { ...body, course_key: courseKey, lesson_id: Number(lessonId), label });
      toast.success(`Sent to students on ${lessonName(lessonId)}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    setBusy(false);
  };
  const remove = async (x) => {
    try { await api.delete(`/admin/lesson-scores/${x.id}`); toast.success("Removed from the lesson"); } catch { toast.error("Could not remove it"); }
    load();
  };
  return (
    <div className="mt-6 rounded-2xl border border-primary/40 bg-primary/5 p-4" data-testid="score-publish">
      <p className="font-mono text-[11px] uppercase tracking-widest text-primary">Send to students</p>
      <p className="mt-1 text-sm text-muted-foreground">Saves this exact version (key, instrument and layout above) as a PDF on a lesson. Only learners on that course can open it.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <select value={courseKey} onChange={(e) => setCourseKey(e.target.value)} className={sel} data-testid="score-publish-course">{courses.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select>
        <select value={lessonId} onChange={(e) => setLessonId(e.target.value)} className={sel} data-testid="score-publish-lesson">{lessons.map((l, i) => <option key={l.id} value={l.id}>Lesson {i + 1} — {l.title}</option>)}</select>
        <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder="Name students will see" className={sel} data-testid="score-publish-label" />
      </div>
      <button type="button" onClick={publish} disabled={busy || !lessonId || !label.trim()} data-testid="score-publish-btn" className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send to lesson
      </button>
      {items.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="score-publish-list">
          {items.map((x) => (
            <li key={x.id} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background px-3 py-2 text-sm" data-testid={`score-published-${x.id}`}>
              <span className="min-w-0 truncate"><strong>{x.label}</strong> · {x.written_key} · {lessonName(x.lesson_id)}</span>
              <button type="button" onClick={() => remove(x)} title="Remove from lesson" data-testid={`score-unpublish-${x.id}`}><Trash2 size={15} className="text-muted-foreground hover:text-destructive" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
